// TODO: Fix all errors

import React, { useEffect, useMemo } from 'react';
import { AppBar, Tabs } from '@material-ui/core';
import { Row } from 'react-bootstrap';
import { twMerge } from 'tailwind-merge';

import { WorkflowType } from 'models/workflow/WorkflowType';
import { safeParseJson } from 'helpers/utils';

const WORKFLOW_TYPE_COLORS = [
    '#6A5C70', // Dark grayish purple
    '#FF6B80', // Muted mauve
    '#FF5A60', // Dusty rose
    '#FF8570', // Peach
    '#FFA070', // Light peach
    '#C27AFF', // Soft lavender
    '#FF70A5', // Pink
    '#50BFFF', // Light blue
    '#30AAFF', // Sky blue
    '#0095FF', // Ocean blue
    '#A455DD', // Wisteria purple
    '#FF55A0', // Rose pink
    '#FF85A5', // Pale pink
    '#FFA090', // Shell pink
    '#FF70FF', // Light purple
    '#805580', // Dusky purple
    '#FF7070', // Vintage rose
    '#E04D70', // Mauve taupe
    '#6B5C65', // Warm gray
    '#7080E0' // Steel blue gray
];

export type WorkflowTypeWithColor = WorkflowType & { color: string };

export type SessionWorkflowTypeWithColor = {
    key: string;
    color: string;
};

const SESSION_PREV_WORKFLOW_TYPE_COLORS_KEY = 'PREV_WORKFLOW_TYPE_COLORS';

export type WorkflowTypeListProps = {
    currentPage: number;
    allWorkflowCountLabel: string;
    workflowTypes: WorkflowType[];
    isPendingWorkflowInbox?: boolean;
    isFetchingWorkflowInbox?: boolean;
    onChangeWorkflowType?: <T extends WorkflowType | 'initial'>(
        workflowType: T,
        index: T extends WorkflowType ? number : undefined
    ) => void;
};

export const WorkflowTypeList = (props: WorkflowTypeListProps) => {
    const {
        currentPage,
        allWorkflowCountLabel,
        workflowTypes,
        isPendingWorkflowInbox,
        isFetchingWorkflowInbox,
        onChangeWorkflowType
    } = props;

    const sortedWorkflowTypes = useMemo(
        () =>
            workflowTypes.slice().sort((a, b) => {
                const aName = a.wftypeName || a.wftype;
                const bName = b.wftypeName || b.wftype;

                if (aName < bName) return -1;
                if (aName > bName) return 1;
                return 0;
            }),
        [workflowTypes]
    );
    const workflowTypesWithColors = useMemo<WorkflowTypeWithColor[]>(() => {
        if (sortedWorkflowTypes.length === 0) return [];

        const saveToSessionAndApplyInitialColors = () => {
            sessionStorage.setItem(
                SESSION_PREV_WORKFLOW_TYPE_COLORS_KEY,
                JSON.stringify(
                    sortedWorkflowTypes.map((type, index) => ({
                        key: type.wftypeName || type.wftype,
                        color: WORKFLOW_TYPE_COLORS[index]
                    }))
                )
            );

            return sortedWorkflowTypes.map((type, index) => ({
                ...type,
                color: WORKFLOW_TYPE_COLORS[index]
            }));
        };

        const rawPrevWorkflowTypeColors = sessionStorage.getItem(
            SESSION_PREV_WORKFLOW_TYPE_COLORS_KEY
        );
        if (!rawPrevWorkflowTypeColors) {
            return saveToSessionAndApplyInitialColors();
        }

        const workflowTypeColors = safeParseJson<
            SessionWorkflowTypeWithColor[]
        >(rawPrevWorkflowTypeColors);
        if (!workflowTypeColors) {
            return saveToSessionAndApplyInitialColors();
        }

        const workflowTypeColorMap = new Map(
            workflowTypeColors.map((type, index) => [
                type.key,
                { color: type.color, position: index }
            ])
        );

        return sortedWorkflowTypes
            .slice()
            .sort((a, b) => {
                const positionA = workflowTypeColorMap.get(
                    a.wftypeName || a.wftype
                )?.position;
                const positionB = workflowTypeColorMap.get(
                    b.wftypeName || b.wftype
                )?.position;

                return (positionA ?? 0) - (positionB ?? 0);
            })
            .map((type, index) => ({
                ...type,
                color:
                    workflowTypeColorMap.get(type.wftypeName || type.wftype)
                        ?.color ?? WORKFLOW_TYPE_COLORS[index]
            }));
    }, [sortedWorkflowTypes]);

    if (workflowTypesWithColors.length === 0) return null;

    return (
        <Row
            className={twMerge(
                'z-[900] mt-2',
                isPendingWorkflowInbox && 'disable-div'
            )}
        >
            <AppBar
                position="static"
                color="inherit"
                className="z-0 shadow-none"
            >
                <Tabs
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="scrollable auto tabs example"
                    value={currentPage}
                    className="z-10"
                >
                    <div
                        className="nav-item"
                        onClick={() =>
                            onChangeWorkflowType?.('initial', undefined)
                        }
                    >
                        <a className="pointer">
                            <div
                                className={twMerge(
                                    'trapezoid trapezoid-purple',
                                    currentPage === 0 && 'active'
                                )}
                                id="trapezoidTapAll"
                                style={{
                                    width: '230px'
                                }}
                            >
                                <span>
                                    <i
                                        className="fa fa-search"
                                        aria-hidden="true"
                                    ></i>
                                    {isFetchingWorkflowInbox
                                        ? 'เอกสาร (0 รายการ)'
                                        : allWorkflowCountLabel}
                                </span>
                            </div>
                        </a>
                    </div>
                    {workflowTypesWithColors.map((workflowType, index) => (
                        <div
                            key={index}
                            className="nav-item"
                            onClick={() =>
                                onChangeWorkflowType?.(workflowType, index)
                            }
                        >
                            <a className="pointer disabled">
                                <div
                                    className={twMerge(
                                        'trapezoid',
                                        currentPage === index + 1 && 'active'
                                    )}
                                    id={'trapezoidTap' + index}
                                    style={{
                                        width: '230px',
                                        borderBottom:
                                            '32px solid ' +
                                            workflowType.color +
                                            ''
                                    }}
                                >
                                    <span>
                                        <i
                                            className="fa fa-search"
                                            aria-hidden="true"
                                        ></i>
                                        {isFetchingWorkflowInbox
                                            ? 'เอกสาร (0 รายการ)'
                                            : (workflowType.wftypeName != null
                                                  ? workflowType.wftypeName
                                                  : workflowType.wftype) +
                                              ' (' +
                                              workflowType.count +
                                              ' รายการ)'}
                                    </span>
                                </div>
                            </a>
                        </div>
                    ))}
                </Tabs>
            </AppBar>
        </Row>
    );
};
